<script setup lang="ts">
import scrollIntoView from 'scroll-into-view-if-needed'

const props = defineProps<{
  item: {
    value: string
    label?: string
  },
  activeId: string | string[] | null,
  scrollIntoView?: boolean,
}>()

const rootTarget = ref<HTMLElement | null>(null)
const isActive = computed(() => Array.isArray(props?.activeId)
  ? props.activeId.includes(props.item.value)
  : props?.activeId === props.item.value)
const isIdle = computed(() => Array.isArray(props?.activeId)
  ? props.activeId.length > 0 && !isActive.value
  : Boolean(props?.activeId) && !isActive.value)

watchDebounced(isActive, (value) => {
  if (value && props.scrollIntoView && rootTarget.value instanceof HTMLElement) {
    const header = document.getElementById('site-header')
    if (header) {
      const inner = header.firstElementChild as HTMLElement
      const bottomPadding = inner ? parseFloat(getComputedStyle(inner).paddingBottom) : 0
      rootTarget.value.style.scrollMarginTop = `${header.offsetHeight - (bottomPadding * 2.5)}px`
    }
    scrollIntoView(rootTarget.value, {
      behavior: 'smooth',
      block: 'start',
      scrollMode: 'if-needed'
    })
  }
}, { debounce: 500 },
)
</script>

<style scoped>
.AccordionContent {
  overflow: hidden;
}
.AccordionContent[data-state="open"] {
  animation: slideDown 150ms ease-out;
}
.AccordionContent[data-state="closed"] {
  animation: slideUp 150ms ease-out;
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--reka-accordion-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--reka-accordion-content-height);
  }
  to {
    height: 0;
  }
}
</style>

<template>
  <AccordionItem
    :value="item.value"
    class="list-sep border-current first:border-current transition-colors duration-150"
  >
    <div ref="rootTarget">
      <AccordionHeader class="flex">
        <AccordionTrigger class="group text-sm grid grid-cols-[1fr_auto] gap-1 w-full data-[state='open']:text-accent hover:text-accent">
          <span class="text-left">
            <slot name="label" :label="item.label">
              {{ item?.label }}
            </slot>
          </span>
          <span
            class="flex items-center justify-center"
          >
            <span
              class="transition-transform group-hover:rotate-45 group-data-[state=open]:rotate-45"
            >
              <svgo-plus class="stroke-sm icon-plus overflow-visible stroke-current" />
            </span>
          </span>
        </AccordionTrigger>
      </AccordionHeader>

      <AccordionContent class="AccordionContent">
        <div class="text-current b-t-current">
          <slot name="content" />
        </div>
      </AccordionContent>
    </div>
  </AccordionItem>
</template>
